import React, { useState } from 'react';
import { Client } from '../types';
import { X, Save, Building, Mail, User, Key, Shield, Calendar, Settings } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

interface ClientSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
  onUpdateClient: (updates: Partial<Client>) => void;
}

export const ClientSettingsModal: React.FC<ClientSettingsModalProps> = ({
  isOpen,
  onClose,
  client,
  onUpdateClient
}) => {
  const [formData, setFormData] = useState({
    name: client.name,
    email: client.email,
    role: client.role,
    isActive: client.isActive,
    apiId: client.apiId || '',
    customSettings: {
      allowDataFeedUpload: client.settings?.allowDataFeedUpload ?? true,
      allowExport: client.settings?.allowExport ?? true,
      maxBannerSets: client.settings?.maxBannerSets ?? 50,
      customBranding: {
        logo: client.settings?.customBranding?.logo || '',
        primaryColor: client.settings?.customBranding?.primaryColor || '#176DB7',
        secondaryColor: client.settings?.customBranding?.secondaryColor || '#1E40AF'
      }
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { showSuccess, showError, showWarning } = useToast();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showError('Validation Error', 'Client name is required');
      return;
    }

    if (!formData.email.trim()) {
      showError('Validation Error', 'Email address is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showError('Validation Error', 'Please enter a valid email address');
      return;
    }

    // Validate API ID format (alphanumeric, hyphens, underscores only)
    if (formData.apiId && !/^[a-zA-Z0-9\-_]+$/.test(formData.apiId)) {
      showError('Validation Error', 'API ID can only contain letters, numbers, hyphens, and underscores');
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      const updates: Partial<Client> = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        role: formData.role,
        isActive: formData.isActive,
        apiId: formData.apiId.trim() || undefined,
        settings: {
          allowDataFeedUpload: formData.customSettings.allowDataFeedUpload,
          allowExport: formData.customSettings.allowExport,
          maxBannerSets: formData.customSettings.maxBannerSets,
          customBranding: formData.customSettings.customBranding
        },
        lastModified: new Date()
      };

      onUpdateClient(updates);
      showSuccess('Client Updated', `${formData.name} settings have been saved successfully`);
      onClose();
    } catch (error) {
      showError('Update Failed', 'Failed to update client settings. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child, grandchild] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent as keyof typeof prev],
          [child]: grandchild ? {
            ...(prev[parent as keyof typeof prev] as any)[child],
            [grandchild]: value
          } : value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const generateApiId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    const clientName = formData.name.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 8);
    const newApiId = `${clientName}-${timestamp}-${random}`;
    
    handleInputChange('apiId', newApiId);
    showSuccess('API ID Generated', 'A unique API ID has been generated for this client');
  };

  const roleOptions = [
    { value: 'admin', label: 'Administrator', description: 'Full access to manage clients and campaigns', icon: Shield },
    { value: 'client', label: 'Client User', description: 'Create and manage banner campaigns', icon: Building },
    { value: 'viewer', label: 'Viewer', description: 'View-only access to campaigns', icon: User }
  ];

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'border-blue-500 bg-blue-50';
      case 'client': return 'border-green-500 bg-green-50';
      case 'viewer': return 'border-gray-500 bg-gray-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Settings className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Client Settings</h2>
              <p className="text-sm text-gray-600">Manage {client.name} account settings and permissions</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Name *
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={isSubmitting}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    placeholder="Enter client name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={isSubmitting}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    placeholder="Enter email address"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* API Integration */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">API Integration</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unique API Identifier
                </label>
                <div className="flex space-x-3">
                  <div className="relative flex-1">
                    <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      value={formData.apiId}
                      onChange={(e) => handleInputChange('apiId', e.target.value)}
                      disabled={isSubmitting}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                      placeholder="e.g., invergrove-honda-2024-abc123"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={generateApiId}
                    disabled={isSubmitting}
                    className="px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    Generate
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  This unique identifier can be used to access this client via API calls. 
                  Leave empty if API access is not needed.
                </p>
              </div>

              {formData.apiId && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">API Access Information</h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <div><strong>Client ID:</strong> <code className="bg-white px-1 rounded">{client.id}</code></div>
                    <div><strong>API ID:</strong> <code className="bg-white px-1 rounded">{formData.apiId}</code></div>
                    <div><strong>Status:</strong> {formData.isActive ? 'Active' : 'Inactive'}</div>
                    <div className="text-xs text-blue-600 mt-2">
                      Use these identifiers when making API calls to access this client's data.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Role and Permissions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Role and Permissions</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Access Level</label>
                <div className="space-y-3">
                  {roleOptions.map((role) => (
                    <label
                      key={role.value}
                      className={`flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${
                        formData.role === role.value ? getRoleColor(role.value) : ''
                      } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role.value}
                        checked={formData.role === role.value}
                        onChange={(e) => handleInputChange('role', e.target.value)}
                        disabled={isSubmitting}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <role.icon size={20} className={formData.role === role.value ? 'text-blue-600' : 'text-gray-500'} />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{role.label}</div>
                        <div className="text-sm text-gray-600">{role.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    disabled={isSubmitting}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Active Account</span>
                    <p className="text-sm text-gray-600">Allow this client to log in and access the system</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Feature Permissions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Feature Permissions</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg">
                  <input
                    type="checkbox"
                    checked={formData.customSettings.allowDataFeedUpload}
                    onChange={(e) => handleInputChange('customSettings.allowDataFeedUpload', e.target.checked)}
                    disabled={isSubmitting}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Data Feed Upload</span>
                    <p className="text-sm text-gray-600">Allow uploading JSON data feeds</p>
                  </div>
                </label>

                <label className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg">
                  <input
                    type="checkbox"
                    checked={formData.customSettings.allowExport}
                    onChange={(e) => handleInputChange('customSettings.allowExport', e.target.checked)}
                    disabled={isSubmitting}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Export Banners</span>
                    <p className="text-sm text-gray-600">Allow exporting and publishing banners</p>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Banner Sets
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={formData.customSettings.maxBannerSets}
                  onChange={(e) => handleInputChange('customSettings.maxBannerSets', parseInt(e.target.value))}
                  disabled={isSubmitting}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum number of banner sets this client can create
                </p>
              </div>
            </div>
          </div>

          {/* Custom Branding */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Custom Branding</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo URL
                </label>
                <input
                  type="url"
                  value={formData.customSettings.customBranding.logo}
                  onChange={(e) => handleInputChange('customSettings.customBranding.logo', e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.customSettings.customBranding.primaryColor}
                      onChange={(e) => handleInputChange('customSettings.customBranding.primaryColor', e.target.value)}
                      disabled={isSubmitting}
                      className="w-12 h-10 border border-gray-300 rounded-lg disabled:opacity-50"
                    />
                    <input
                      type="text"
                      value={formData.customSettings.customBranding.primaryColor}
                      onChange={(e) => handleInputChange('customSettings.customBranding.primaryColor', e.target.value)}
                      disabled={isSubmitting}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                      placeholder="#176DB7"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.customSettings.customBranding.secondaryColor}
                      onChange={(e) => handleInputChange('customSettings.customBranding.secondaryColor', e.target.value)}
                      disabled={isSubmitting}
                      className="w-12 h-10 border border-gray-300 rounded-lg disabled:opacity-50"
                    />
                    <input
                      type="text"
                      value={formData.customSettings.customBranding.secondaryColor}
                      onChange={(e) => handleInputChange('customSettings.customBranding.secondaryColor', e.target.value)}
                      disabled={isSubmitting}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                      placeholder="#1E40AF"
                    />
                  </div>
                </div>
              </div>

              {/* Branding Preview */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Branding Preview</h4>
                <div className="flex items-center space-x-4">
                  {formData.customSettings.customBranding.logo && (
                    <img
                      src={formData.customSettings.customBranding.logo}
                      alt="Client Logo"
                      className="w-12 h-12 object-contain rounded border border-gray-200 bg-white"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                  <div className="flex space-x-2">
                    <div
                      className="w-8 h-8 rounded border border-gray-200"
                      style={{ backgroundColor: formData.customSettings.customBranding.primaryColor }}
                      title="Primary Color"
                    />
                    <div
                      className="w-8 h-8 rounded border border-gray-200"
                      style={{ backgroundColor: formData.customSettings.customBranding.secondaryColor }}
                      title="Secondary Color"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Account Created:</span>
                  <p className="text-gray-900">{formatDate(client.createdAt)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Last Login:</span>
                  <p className="text-gray-900">{formatDate(client.lastLogin)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Banner Sets:</span>
                  <p className="text-gray-900">{client.bannerSets.length} created</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Account Status:</span>
                  <p className={`font-medium ${client.isActive ? 'text-green-600' : 'text-red-600'}`}>
                    {client.isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Saving changes...</span>
              </div>
            ) : (
              'Changes will be saved immediately'
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.name.trim() || !formData.email.trim()}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={16} />
              <span>{isSubmitting ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};